import { CodeInfo, EmailInfo } from '~/_v3/activation/domain/activation';
import { ApiError } from '~/_v3/common/actors/errors';
import { apiRequest } from '~/_v3/common/services/httpService';
import { getToken, removeToken, setAuthToken, setChatbotToken, setToken } from '~/_v3/composables/useToken';

const baseUrl = import.meta.env.SILAE_EDP_BACKEND_URL;

export type LoginConfig = {
	franceConnectUrl: string;
};

export type LoginRequest = {
	email: string;
	password: string;
	franceConnectSub?: string;
};

export type LoginResponse = {
	userId: string;
	token: string;
	refreshToken: string;
	invalidToken?: string;
	foundInvalidUserToMerge?: boolean;
	loginUrl?: string;
};

interface VerifyEmailResponse {
	isEmailAlreadyUsed: boolean;
}

const getTokensToMerge = () => {
	let tokenToKeep;
	let tokenToMerge;
	// when we are trying to connect to an invalidUser, it is the invalidUser account that needs to be merged in the other detected account
	if (getToken('invalidUserToken') !== null) {
		tokenToKeep = getToken('mergeToken');
		tokenToMerge = getToken('invalidUserToken');
	} else {
		tokenToKeep = getToken('authToken');
		tokenToMerge = getToken('mergeToken');
	}

	return { tokenToKeep, tokenToMerge };
};

export const fetchFranceConnectUrlFromServer = async (): Promise<void> => {
	const response = await fetch(baseUrl + '/api/v1/auth/franceconnect/init', {
		method: 'GET',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json'
		}
	});

	if (!response.ok) {
		const errorBody = await response.json();
		throw new ApiError(`Failed to get config: ${response.status}`, errorBody);
	}
	const json = await response.json();
	return json;
};

export const login = async ({ input }: { input: LoginRequest }) => {
	const response = await fetch(baseUrl + '/api/v1/login', {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(input)
	});

	const responseBody: LoginResponse = await response.json();

	const token: string | null = response.headers.get('Set-Authorization');

	if (!token && !responseBody.loginUrl) {
		throw new ApiError(`Failed to login: ${response.status}`, responseBody);
	}

	if (token) {
		const payload = JSON.parse(atob(token.split('.')[1]));
		// when when there is an edpv2 account migrated in v3 with an invalid email, we receive a token with type JwtPayloadLoggedInvalidUser
		if (payload.type !== 'JwtPayloadLoggedInvalidUser') {
			setAuthToken(token);
			if (input.franceConnectSub) {
				setToken('franceConnectToken', token);
			}
		} else {
			setToken('invalidUserToken', token);
		}
		// when there is an edpv2 account migrated in v3 with the same email and the same password as an existing v3 account, we receive an invalidToken in response,
		// which will be used to merge the migrated account inside the new v3 account
		if (responseBody.invalidToken) {
			localStorage.setItem('mergeToken', responseBody.invalidToken);
		}
	}

	return responseBody;
};

export const checkJwt = async () => {
	const token = localStorage.getItem('authToken');
	if (!token) {
		const errorBody = { errors: ['jwt_not_found'] };
		throw new ApiError(`Jwt not found`, errorBody);
	}

	// Parse the JWT payload (second part) and check expiration
	let payload = undefined;
	try {
		payload = JSON.parse(atob(token.split('.')[1]));
	} catch (error) {
		const errorBody = { errors: ['invalid_jwt_format'] };
		throw new ApiError(`Invalid JWT format`, errorBody);
	}
	const isExpired = payload.exp * 1000 < Date.now(); // Convert exp to milliseconds

	if (isExpired) {
		const errorBody = { errors: ['jwt_expired'] };
		throw new ApiError(`Jwt has expired`, errorBody);
	}
};

export const refreshToken = async () => {
	const currentToken = localStorage.getItem('authToken');
	if (!currentToken) {
		const errorBody = { errors: ['jwt_not_found'] };
		throw new ApiError(`Jwt not found`, errorBody);
	}
	const response = await fetch(baseUrl + '/api/v1/auth/refresh-token', {
		method: 'GET',
		headers: {
			Accept: 'application/json',
			Authorization: 'Bearer ' + currentToken
		}
	});

	const token: string | null = response.headers.get('Set-Authorization');
	if (!token) {
		const errorBody = await response.json();
		console.warn(`Failed to refresh token: ${response.status}`, errorBody); // we do not throw nay error here
		return;
	}
	setAuthToken(token);
};

export const getChatbotToken = async (): Promise<void> => {
	const authToken = getToken('authToken');
	const response = await apiRequest(`/api/v1/auth/ago/get-jwt`, 'GET', authToken);

	setChatbotToken(response.jwt);

	return response;
};

export const sendEmail = async ({ input }: { input: EmailInfo }): Promise<void> => {
	const invalidUserToken = getToken('invalidUserToken');

	return apiRequest(`/api/v1/user/request-email-change`, 'POST', invalidUserToken, input);
};

export const verifyEmailCode = async ({ input }: { input: CodeInfo }): Promise<VerifyEmailResponse> => {
	const invalidUserToken = getToken('invalidUserToken');

	return apiRequest(`/api/v1/user/change-email`, 'POST', invalidUserToken, input);
};

export const validateUser = async () => {
	const invalidUserToken = getToken('invalidUserToken');

	const response = await fetch(baseUrl + '/api/v1/user/validate', {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			Authorization: 'Bearer ' + invalidUserToken
		}
	});

	const token: string | null = response.headers.get('Set-Authorization');

	if (!token) {
		const errorBody = await response.json();
		throw new ApiError(`Failed to login: ${response.status}`, errorBody);
	}

	removeToken('invalidUserToken');

	setAuthToken(token);
};

export const getInfoForMerge = async () => {
	const { tokenToKeep, tokenToMerge } = getTokensToMerge();
	const response = await fetch(baseUrl + '/api/v1/user/info-for-merge', {
		method: 'GET',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			Authorization: 'Bearer ' + tokenToKeep,
			'x-user-to-merge-token': tokenToMerge
		}
	});

	if (!response.ok) {
		const errorBody = await response.json();
		throw new ApiError(`Failed to activate: ${response.status}`, errorBody);
	}

	return response.json();
};

export const merge = async () => {
	const { tokenToKeep, tokenToMerge } = getTokensToMerge();

	const response = await fetch(baseUrl + '/api/v1/user/merge', {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			Authorization: 'Bearer ' + tokenToKeep,
			'x-user-to-merge-token': tokenToMerge
		}
	});

	if (!response.ok) {
		const errorBody = await response.json();
		throw new ApiError(`Failed to activate: ${response.status}`, errorBody);
	}

	const authToken = tokenToKeep;

	if (authToken) {
		setAuthToken(authToken);
	}

	removeToken('invalidUserToken');
	removeToken('mergeToken');
};
